import React from "react";

import type { PatientDobFilters } from "./patient_search";

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

interface PatientDobFilterToggleProps {
  controlsId: string;
  expanded: boolean;
  filters: PatientDobFilters;
  onToggle: () => void;
}

interface PatientDobFilterFieldsProps {
  filters: PatientDobFilters;
  id: string;
  onChange: React.Dispatch<React.SetStateAction<PatientDobFilters>>;
}

export function PatientDobFilterToggle({ controlsId, expanded, filters, onToggle }: PatientDobFilterToggleProps) {
  const activeFilterCount = Object.values(filters).filter(Boolean).length;
  const label = `${expanded ? "Hide" : "Show"} date of birth filters`;

  return (
    <button
      type="button"
      aria-controls={controlsId}
      aria-expanded={expanded}
      aria-label={label}
      title={label}
      className={`inline-flex h-7 w-7 items-center justify-center rounded-md transition ${
        activeFilterCount > 0
          ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-950/50 dark:text-emerald-300 dark:hover:bg-emerald-950"
          : "text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700 dark:text-neutral-500 dark:hover:bg-neutral-800 dark:hover:text-neutral-200"
      }`}
      onClick={onToggle}
    >
      <svg
        aria-hidden="true"
        className={`h-4 w-4 transition-transform ${expanded ? "rotate-180" : ""}`}
        fill="none"
        viewBox="0 0 20 20"
        stroke="currentColor"
        strokeWidth="2"
      >
        <path d="m5 7.5 5 5 5-5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      {activeFilterCount > 0 ? (
        <span className="sr-only">{activeFilterCount} active date of birth filters</span>
      ) : null}
    </button>
  );
}

export function PatientDobFilterFields({ filters, id, onChange }: PatientDobFilterFieldsProps) {
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: currentYear - 1899 }, (_, index) => currentYear - index);
  const activeFilterCount = Object.values(filters).filter(Boolean).length;

  const updateFilter = (field: keyof PatientDobFilters, value: string) => {
    onChange((currentFilters) => ({ ...currentFilters, [field]: value }));
  };

  const clearFilters = () => onChange({ year: "", month: "", day: "" });

  return (
    <div id={id} className="mt-2 rounded-lg border border-neutral-200 bg-neutral-50/70 p-3 dark:border-neutral-800 dark:bg-neutral-950/50">
      <div className="flex items-center justify-between gap-2">
        <div>
          <p className="text-sm font-semibold text-neutral-950 dark:text-white">Filter by date of birth</p>
          <p className="mt-0.5 text-xs text-neutral-500 dark:text-neutral-400">Use any combination of fields.</p>
        </div>
        {activeFilterCount > 0 ? (
          <button type="button" className="text-xs font-medium text-emerald-700 hover:underline dark:text-emerald-300" onClick={clearFilters}>
            Clear
          </button>
        ) : null}
      </div>

      <div className="mt-3 grid grid-cols-3 gap-2 sm:max-w-md">
        <label className="min-w-0 text-xs font-medium text-neutral-600 dark:text-neutral-300">
          Year
          <select
            aria-label="Birth year"
            className="app-input mt-1 px-2"
            value={filters.year}
            onChange={(event) => updateFilter("year", event.target.value)}
          >
            <option value="">Any</option>
            {years.map((year) => <option key={year} value={year}>{year}</option>)}
          </select>
        </label>

        <label className="min-w-0 text-xs font-medium text-neutral-600 dark:text-neutral-300">
          Month
          <select
            aria-label="Birth month"
            className="app-input mt-1 px-2"
            value={filters.month}
            onChange={(event) => updateFilter("month", event.target.value)}
          >
            <option value="">Any</option>
            {MONTHS.map((month, index) => <option key={month} value={index + 1}>{month.slice(0, 3)}</option>)}
          </select>
        </label>

        <label className="min-w-0 text-xs font-medium text-neutral-600 dark:text-neutral-300">
          Day
          <select
            aria-label="Birth day"
            className="app-input mt-1 px-2"
            value={filters.day}
            onChange={(event) => updateFilter("day", event.target.value)}
          >
            <option value="">Any</option>
            {Array.from({ length: 31 }, (_, index) => index + 1).map((day) => <option key={day} value={day}>{day}</option>)}
          </select>
        </label>
      </div>
    </div>
  );
}
